/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Manifest API.
 *
 * The manifest is fetched once at app startup (after auth) and rarely
 * mutates. We don't paginate it - it's a single JSON document.
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { Manifest } from "../model/types";

/** RTK Query endpoint for the current tenant's manifest. */
export const manifestApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCurrentManifest: build.query<Manifest, void>({
      query: () => "/manifest/current/",
      providesTags: ["Manifest"],
    }),
    updateManifest: build.mutation<Manifest, Manifest & { _change_summary?: string }>({
      query: (body) => {
        const payload = { ...body } as Record<string, unknown>;
        delete payload.vocab_overrides;
        delete payload.product_name;
        return {
          url: "/manifest/update/",
          method: "POST",
          body: payload,
        };
      },
      transformResponse: (response: { content?: Manifest } | Manifest) =>
        "content" in response && response.content ? response.content : (response as Manifest),
      invalidatesTags: ["Manifest"],
    }),
    patchManifestCustomization: build.mutation<
      { vocab_overrides: Record<string, unknown>; product_name: string },
      { vocab_overrides?: Record<string, unknown>; product_name?: string }
    >({
      query: (body) => ({
        url: "/manifest/customization/",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Manifest"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCurrentManifestQuery,
  useUpdateManifestMutation,
  usePatchManifestCustomizationMutation,
} = manifestApi;
